import { useEffect } from "react";
import PanamaCityHeroSection from "./components/HeroSection";
import PanamaCityStickyNav from "./components/StickyNav";
import PanamaCityProcessingTimeSection from "./components/ProcessingTimeSection";
import PanamaCityServicesSection from "./components/ServicesSection";
import PanamaCityLocalAreaSection from "./components/LocalAreaSection";
import PanamaCityTestimonialsSection from "./components/TestimonialsSection";
import PanamaCityAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import PanamaCityComparisonSection from "./components/ComparisonSection";
import PanamaCityMistakesSection from "./components/MistakesSection";
import PanamaCityHowItWorksSection from "./components/HowItWorksSection";
import PanamaCityFAQSection, { faqs } from "./components/FAQSection";
import PanamaCityHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import PanamaCityFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function PanamaCityPage() {
  useEffect(() => {
    document.title = "Emergency Passport Panama City, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/panama-city/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Panama City, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving the Florida Panhandle and Emerald Coast.");
    setMeta("geo.placename", "Panama City, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "30.1588;-85.6602");
    setMeta("ICBM", "30.1588, -85.6602");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Panama City Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Panama City — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Panama City",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Panama City",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <PanamaCityHeroSection />
      <PanamaCityStickyNav />
      <PanamaCityProcessingTimeSection />
      <PanamaCityServicesSection />
      <PanamaCityLocalAreaSection />
      <PanamaCityTestimonialsSection />
      <PanamaCityAcceptanceFacilitiesSection />
      <PanamaCityComparisonSection />
      <PanamaCityMistakesSection />
      <PanamaCityHowItWorksSection />
      <PanamaCityFAQSection />
      <PanamaCityHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/panama-city" state="FL" />
      <PanamaCityFinalCTA />
    </div>
  );
}